import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Check,
  Copy,
  KeyRound,
  Mail,
  Pencil,
  Plus,
  Power,
  RefreshCw,
  Search,
  Trash2,
  UserPlus,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { ErrorState } from "@/components/shared/error-state";
import { PageHeader } from "@/components/shared/page-header";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useCompanies } from "@/hooks/use-companies";
import { apiDelete, apiFetch, apiPatch, apiPost, apiPut } from "@/lib/api";
import { userKeys } from "@/lib/query";

interface AppUser {
  id: string;
  name: string | null;
  email: string;
  role: string | null;
  companyId: string | null;
  companyName: string | null;
  status: string | null;
  isActive: boolean;
}

const ROLE_OPTIONS = [
  { value: "admin", label: "Admin" },
  { value: "project_manager", label: "Project Manager" },
  { value: "supervisor", label: "Superintendent" },
  { value: "contractor", label: "Contractor" },
];

function roleVariant(role: string | null): "default" | "secondary" | "destructive" {
  switch (role) {
    case "admin":
      return "destructive";
    case "project_manager":
    case "supervisor":
      return "default";
    default:
      return "secondary";
  }
}

function formatRoleLabel(role: string | null): string {
  if (!role) return "--";
  if (role === "supervisor") return "Superintendent";
  return role
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

export function AdminUsersPage() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editUser, setEditUser] = useState<AppUser | null>(null);
  const [deleteUser, setDeleteUser] = useState<AppUser | null>(null);
  const [setPasswordUser, setSetPasswordUser] = useState<AppUser | null>(null);
  const [tempPassword, setTempPassword] = useState("");
  const [copied, setCopied] = useState(false);

  // Form state
  const [formName, setFormName] = useState("");
  const [formEmail, setFormEmail] = useState("");
  const [formRole, setFormRole] = useState("");
  const [formCompanyId, setFormCompanyId] = useState("");
  const [formCompanyName, setFormCompanyName] = useState("");

  const { data: companies } = useCompanies();

  const {
    data: users,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: userKeys.all,
    queryFn: () => apiFetch<AppUser[]>("/users"),
  });

  const createMutation = useMutation({
    mutationFn: (data: Partial<AppUser>) => apiPost<{ data: AppUser }>("/users", data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: userKeys.all });
      toast.success("User created");
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, ...data }: { id: string } & Partial<AppUser>) =>
      apiPut<{ data: AppUser }>(`/users/${id}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: userKeys.all });
      toast.success("User updated");
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiDelete(`/users/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: userKeys.all });
      toast.success("User deleted");
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const inviteMutation = useMutation({
    mutationFn: (id: string) => apiPost(`/users/${id}/invite`),
    onSuccess: () => toast.success("Invite sent"),
    onError: (err: Error) => toast.error(err.message),
  });

  const statusMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      apiPatch<{ data: AppUser }>(`/users/${id}/status`, { isActive }),
    onSuccess: (_res, vars) => {
      qc.invalidateQueries({ queryKey: userKeys.all });
      toast.success(vars.isActive ? "User activated" : "User deactivated");
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const setPasswordMutation = useMutation({
    mutationFn: ({ id, password }: { id: string; password: string }) =>
      apiPost(`/users/${id}/set-temp-password`, { password }),
    onSuccess: () => toast.success("Temporary password set"),
    onError: (err: Error) => toast.error(err.message),
  });

  function generatePassword() {
    const words = ["Oak", "Blue", "River", "Crane", "Stone", "Maple", "Hawk", "Cedar"];
    const w1 = words[Math.floor(Math.random() * words.length)];
    const w2 = words[Math.floor(Math.random() * words.length)];
    const num = Math.floor(Math.random() * 90 + 10);
    const sym = ["!", "@", "#", "$"][Math.floor(Math.random() * 4)];
    return `${w1}${w2}${num}${sym}`;
  }

  function openSetPasswordDialog(user: AppUser) {
    setSetPasswordUser(user);
    setTempPassword(generatePassword());
    setCopied(false);
  }

  function handleCopyCredentials() {
    if (!setPasswordUser) return;
    const text = `Email: ${setPasswordUser.email}\nPassword: ${tempPassword}`;
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  function handleSetPassword() {
    if (!setPasswordUser) return;
    setPasswordMutation.mutate(
      { id: setPasswordUser.id, password: tempPassword },
      { onSuccess: () => setSetPasswordUser(null) },
    );
  }

  function openCreateDialog() {
    setEditUser(null);
    setFormName("");
    setFormEmail("");
    setFormRole("");
    setFormCompanyId("");
    setFormCompanyName("");
    setDialogOpen(true);
  }

  function openEditDialog(user: AppUser) {
    setEditUser(user);
    setFormName(user.name ?? "");
    setFormEmail(user.email);
    setFormRole(user.role ?? "");
    setFormCompanyId(user.companyId ?? "");
    setFormCompanyName(user.companyName ?? "");
    setDialogOpen(true);
  }

  function handleSave() {
    const payload = {
      name: formName,
      email: formEmail,
      role: formRole || null,
      companyId: formCompanyId || null,
      companyName: formCompanyName || null,
    };

    if (editUser) {
      updateMutation.mutate(
        { id: editUser.id, ...payload },
        { onSuccess: () => setDialogOpen(false) },
      );
    } else {
      createMutation.mutate(payload, {
        onSuccess: () => setDialogOpen(false),
      });
    }
  }

  function handleDelete() {
    if (!deleteUser) return;
    deleteMutation.mutate(deleteUser.id, {
      onSuccess: () => setDeleteUser(null),
    });
  }

  const isSaving = createMutation.isPending || updateMutation.isPending;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <ErrorState message={error?.message ?? "Failed to load users"} onRetry={() => refetch()} />
    );
  }

  const allUsers = users ?? [];
  const filtered = search.trim()
    ? allUsers.filter(
        (u) =>
          (u.name ?? "").toLowerCase().includes(search.toLowerCase()) ||
          u.email.toLowerCase().includes(search.toLowerCase()),
      )
    : allUsers;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Users"
        description="Manage user accounts and permissions"
        actions={
          <Button onClick={openCreateDialog}>
            <Plus className="mr-2 h-4 w-4" />
            Add User
          </Button>
        }
      />

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search users..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      {filtered.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <UserPlus className="mx-auto mb-3 h-10 w-10 text-muted-foreground/50" />
            <p>No users found.</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Company</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-56" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((user) => (
                <TableRow key={user.id}>
                  <TableCell className="font-medium">{user.name ?? "--"}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <Badge variant={roleVariant(user.role)}>{formatRoleLabel(user.role)}</Badge>
                  </TableCell>
                  <TableCell>{user.companyName ?? "--"}</TableCell>
                  <TableCell>
                    <Badge
                      variant={
                        user.status === "active"
                          ? "success"
                          : user.status === "inactive"
                            ? "destructive"
                            : "secondary"
                      }
                    >
                      {user.status ?? "pending"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => inviteMutation.mutate(user.id)}
                        disabled={inviteMutation.isPending}
                        title="Send Invite"
                      >
                        <Mail className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openSetPasswordDialog(user)}
                        title="Set Temporary Password"
                      >
                        <KeyRound className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() =>
                          statusMutation.mutate({ id: user.id, isActive: !user.isActive })
                        }
                        disabled={statusMutation.isPending}
                        title={user.isActive ? "Deactivate" : "Activate"}
                      >
                        <Power
                          className={`h-4 w-4 ${user.isActive ? "text-green-600" : "text-muted-foreground"}`}
                        />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openEditDialog(user)}
                        title="Edit"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setDeleteUser(user)}
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editUser ? "Edit User" : "Add User"}</DialogTitle>
            <DialogDescription>
              {editUser
                ? "Update user details below."
                : "Fill in the details to create a new user."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder="Full name"
              />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={formEmail}
                onChange={(e) => setFormEmail(e.target.value)}
                placeholder="user@example.com"
              />
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={formRole} onValueChange={setFormRole}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  {ROLE_OPTIONS.map((r) => (
                    <SelectItem key={r.value} value={r.value}>
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Company</Label>
              <Select
                value={formCompanyId}
                onValueChange={(val) => {
                  setFormCompanyId(val);
                  const company = (companies ?? []).find((c) => c.id === val);
                  if (company) setFormCompanyName(company.companyName);
                }}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select company" />
                </SelectTrigger>
                <SelectContent>
                  {(companies ?? []).map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.companyName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={isSaving || !formName.trim() || !formEmail.trim()}
            >
              {isSaving ? "Saving..." : editUser ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Set Temporary Password Dialog */}
      <Dialog
        open={setPasswordUser !== null}
        onOpenChange={(open) => {
          if (!open) setSetPasswordUser(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Set Temporary Password</DialogTitle>
            <DialogDescription>
              A one-time password for{" "}
              <strong>{setPasswordUser?.name ?? setPasswordUser?.email}</strong>. The user will be
              required to change it on next login.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Temporary Password</Label>
              <div className="flex gap-2">
                <Input
                  value={tempPassword}
                  onChange={(e) => setTempPassword(e.target.value)}
                  className="font-mono"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => {
                    setTempPassword(generatePassword());
                    setCopied(false);
                  }}
                  title="Generate new password"
                >
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="rounded-lg border bg-muted/50 p-4 space-y-3">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Credentials to share
              </p>
              <div className="font-mono text-sm space-y-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-muted-foreground">Email</span>
                  <span className="truncate">{setPasswordUser?.email}</span>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-muted-foreground">Password</span>
                  <span>{tempPassword}</span>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={handleCopyCredentials}
              >
                {copied ? (
                  <>
                    <Check className="mr-2 h-4 w-4 text-green-500" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="mr-2 h-4 w-4" />
                    Copy Credentials
                  </>
                )}
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSetPasswordUser(null)}>
              Cancel
            </Button>
            <Button
              onClick={handleSetPassword}
              disabled={setPasswordMutation.isPending || !tempPassword.trim()}
            >
              {setPasswordMutation.isPending ? "Setting..." : "Set Password"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog
        open={deleteUser !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteUser(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {deleteUser?.name ?? deleteUser?.email}? This action
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>
              {deleteMutation.isPending ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
